import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './App.css';
import './Katanaut.css';
import Navbar from './Navbar';
import LazyVideo from './LazyVideo';
import SiteFooter from './SiteFooter';
import { getGame } from './games';
import usePrefersReducedMotion from './usePrefersReducedMotion';
import steamlogo from './images/logos/steam.webp';
// logo.png is the 5829px master; logo-web.png is the downscaled copy actually
// shown here (it never renders wider than ~1100px).
import logo from './images/logo-web.png';
import headerPoster from './images/header-poster.jpg';
import headerVideoWebm from './videos/header_video.webm';
import headerVideoMp4 from './videos/header_video.mp4';
import video1 from './videos/vid1.webm';
import video2 from './videos/vid2.webm';
import video3 from './videos/vid3.webm';
import video4 from './videos/vid4.webm';

// Store and press links live in games.js so the navbar, the wishlist button and
// the landing page can't drift apart.
const GAME = getGame('katanaut');

const KATANAUT_DESCRIPTION =
  'Katanaut is a fast-paced action-roguelite unfolding within the eerie, decaying halls of a massive space station. Following a mysterious catastrophe, the station has become home to relentless enemies, hidden relics, and blood-soaked horrors.';

function Katanaut() {
  const [isMobile, setIsMobile] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    const minimumDisplayMs = 450;

    const loadImage = (src) => new Promise((resolve) => {
      const image = new Image();
      image.onload = resolve;
      image.onerror = resolve;
      image.src = src;
      if (image.complete) resolve();
    });

    const warmVideo = fetch(headerVideoWebm, { cache: 'force-cache' }).catch(() => {});
    const criticalAssets = Promise.all([
      loadImage(logo),
      loadImage(headerPoster),
      loadImage(steamlogo),
      warmVideo,
    ]);

    const reveal = () => {
      const remaining = Math.max(0, minimumDisplayMs - (Date.now() - startedAt));
      window.setTimeout(() => {
        if (!cancelled) setIsReady(true);
      }, remaining);
    };

    criticalAssets.then(reveal);
    const safetyTimer = window.setTimeout(() => {
      if (!cancelled) setIsReady(true);
    }, 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(safetyTimer);
    };
  }, []);

  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkScreenSize);
    checkScreenSize();
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const scrollToVideo = () => {
    const videoElement = document.querySelector('.header-video-container');
    if (videoElement) {
      const navbarHeight = document.querySelector('.navbar').offsetHeight;
      const videoPosition = videoElement.getBoundingClientRect().top + window.scrollY - navbarHeight - 20;
      window.scrollTo({ top: videoPosition, behavior: 'smooth' });
    }
  };

  const openSteamPage = () => {
    window.open(GAME.steamUrl, '_blank');
  };

  const scrollToContent = () => {
    const contentElement = document.querySelector('.App-content');
    if (contentElement) {
      const navbarHeight = document.querySelector('.navbar').offsetHeight;
      const contentPosition = contentElement.getBoundingClientRect().top + window.scrollY - navbarHeight - 20;
      window.scrollTo({ top: contentPosition, behavior: 'smooth' });
    }
  };

  const handleVideoClick = (event) => {
    const videoElement = event.target;
    if (videoElement.requestFullscreen) {
      videoElement.requestFullscreen();
    } else if (videoElement.mozRequestFullScreen) {
      videoElement.mozRequestFullScreen();
    } else if (videoElement.webkitRequestFullscreen) {
      videoElement.webkitRequestFullscreen();
    } else if (videoElement.msRequestFullscreen) {
      videoElement.msRequestFullscreen();
    }
  };

  const handleVideoError = (e) => {
    console.error('Video failed to load', e);
  };

  return (
    <div className={`App katanaut-page${isReady ? ' katanaut-page-ready' : ' katanaut-page-loading'}`} aria-busy={!isReady}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IM+Fell+English&family=Spectral:ital,wght@0,400;1,400&display=swap" />
        <title>Katanaut</title>
        <meta name="description" content={KATANAUT_DESCRIPTION} />
        <meta property="og:url" content="https://www.katanaut.com" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Katanaut" />
        <meta property="og:description" content={KATANAUT_DESCRIPTION} />
        <meta property="og:image" content="https://i.imgur.com/QHYTfGa.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="katanaut.com" />
        <meta property="twitter:url" content="https://www.katanaut.com" />
        <meta name="twitter:title" content="Katanaut" />
        <meta name="twitter:description" content={KATANAUT_DESCRIPTION} />
        <meta name="twitter:image" content="https://i.imgur.com/QHYTfGa.png" />
      </Helmet>
      <div className="katanaut-loader" role="status" aria-live="polite" aria-label="Loading Katanaut">
        <span className="katanaut-loader-label">Receiving transmission</span>
        <span className="katanaut-loader-line" aria-hidden="true"><i /></span>
        <span className="katanaut-loader-code" aria-hidden="true">KAT / 001</span>
      </div>
      <Navbar pressHref={GAME.pressHref} steamHref={GAME.steamUrl} highlightPress />
      <div className="header-container">
        {/* The poster paints the key art immediately, so the header is never
            blank while the loop streams in. Source order matters: everything
            modern takes the WebM, and the H.264 copy is only fetched by
            browsers without WebM (notably iOS Safari before 17.4). */}
        <video
          className="header-image"
          poster={headerPoster}
          // Reserves 16:9 before metadata arrives, so the page doesn't jolt
          // downward once the loop loads.
          width={1920}
          height={1080}
          // Reduced-motion visitors keep the poster instead of the loop.
          autoPlay={!reducedMotion}
          loop
          muted={true}
          playsInline
          onError={handleVideoError}
        >
          <source src={headerVideoWebm} type="video/webm" />
          <source src={headerVideoMp4} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="logo-and-buttons">
          <img src={logo} alt="Katanaut Logo" className="header-logo" width="1800" height="407" />
          <div className="button-container">
            <button className="store-button" onClick={openSteamPage}>
              {GAME.ctaLabel}
            </button>
            <button className="trailer-button" onClick={scrollToVideo}>
              TRAILER
            </button>
          </div>
          <div className="steam-logo-container" onClick={openSteamPage}>
            <span className="release-date">Out now on&nbsp;</span>
            <img src={steamlogo} alt="Steam" className="steam-logo" width="196" height="60" />
          </div>
          {/* ABOUT was a third competing button for what is just "scroll down". */}
          <button
            className="scroll-cue"
            onClick={scrollToContent}
            aria-label="Scroll to game details"
          >
            <span>About the game</span>
            <span className="scroll-cue-chevron" aria-hidden="true" />
          </button>
          {/* <div className="press-release-container">
            <div className="press-release-card">
              <p className="press-release-preview">
                <b>Katanaut Slices Through Space and Steam This September</b><br />
                September 10th release date announced during Future Games Show Live from Gamescom 2025!
              </p>
              <a
                href="https://press.piratepr.com/katanaut-slices-through-space-and-steam-this-september"
                target="_blank"
                rel="noopener noreferrer"
                className="press-release-button"
              >
                PRESS RELEASE
              </a>
            </div>
          </div> */}
        </div>
      </div>
      <div className={`header-video-container ${isMobile ? 'mobile-header' : ''}`}>
        <iframe
          className="header-video"
          src="https://www.youtube.com/embed/A2931LjzLwQ"
          title="Katanaut Trailer"
          loading="lazy"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
      <div className="App-content">
        <h1 className="main-title">What is Katanaut?</h1>
        <div className="description-container">
          <p>
            <b>Katanaut</b> is an action-roguelite set aboard a massive space station falling into cosmic chaos. With the station slowly spiraling out of control, and its once-human inhabitants twisted into terrors, you’re the unlucky one tasked with uncovering what is going on... assuming you’re able to survive the station's violent halls with souls-like combat.
          </p>
          <div className="videos">
            <LazyVideo
              src={video1}
              poster="/Press/previews/Residential1.webp"
              className="video"
              width={800}
              height={274}
              onClick={handleVideoClick}
              onError={handleVideoError}
            />
          </div>
          <p>
            As Naut, you explore the corridors of this once vibrant station... or what's left of it. The deeper you descend into the station, the more haunting the environments become, and the less human the enemies appear.
          </p>
          <p>
            Through advanced time-manipulation, you’re brought back to life whenever your biometrics are no longer detected. In the future where humans are able to download new abilities on demand, the station is now scattered with memory fragments from the fallen. Each one may hold a lost skill or talent waiting to be unlocked.
          </p>
          <div className="videos">
            <LazyVideo
              src={video2}
              poster="/Press/previews/Commercial3.webp"
              className="video"
              width={800}
              height={274}
              onClick={handleVideoClick}
              onError={handleVideoError}
            />
          </div>
          <p>
            Every run offers a new challenge, with ever-changing pathways to explore and hordes of enemies to face. Each ride down the station's elevators carries forward progress from the memory fragments you scavenge along the way.
          </p>
          <p>
            In Katanaut, each clash of your blade echoes through the station's empty and flickering corridors, where something seems to be watching you around every corner... Whether you thrive in precision combat or prefer dealing with enemies from a safe distance, this is one cleanup job you won't forget.
          </p>
        </div>
        <div className="feature-grid">
          <p><b style={{ color: '#d0be54' }}>Diverse Builds:</b> Experiment with different weapons, guns, and abilities to create a strategy that matches your style, or something completely unexpected.</p>
          <p><b style={{ color: '#d0be54' }}>Unique Challenges:</b> Experience a unique mixture of hand-crafted and procedurally generated levels where it could be a quick and easy run, or an intense onslaught of surprises.</p>
          <p><b style={{ color: '#d0be54' }}>Unlockable Arsenal:</b> Roguelites aren't roguelites without unlockable items! Katanaut offers a vast number of weapons, skills, and perks waiting to be discovered and upgraded.</p>
          <p><b style={{ color: '#d0be54' }}>High-Stakes Combat:</b> Got quick reflexes? We serve up speedy fights where playing smart and thinking quickly can make all the difference.</p>
          <p><b style={{ color: '#d0be54' }}>Pixel-Perfect Design:</b> Experience a pixel-art universe where all the details are refined and designed by hand. All to inspire the unsettling beauty of this cosmic nightmare!</p>
          <p><b style={{ color: '#d0be54' }}>Synthwave:</b> Our soundtrack brings immersive energy to every battle and simultaneously aims to paint the vibe of the station’s darkest corners – all made by one immensely talented musician!</p>
        </div>
        <div className="videos">
          <LazyVideo
            src={video3}
              poster="/Press/previews/Labs2.webp"
            className="header-image"
            width={800}
            height={274}
            onClick={handleVideoClick}
            onError={handleVideoError}
          />
          <div className="videos">
            <LazyVideo
              src={video4}
              poster="/Press/previews/Reactor1.webp"
              className="video"
              width={800}
              height={274}
              onClick={handleVideoClick}
              onError={handleVideoError}
            />
          </div>
        </div>
      </div>
      <SiteFooter showContact={false} steamHref={GAME.steamUrl} />
    </div>
  );
}

export default Katanaut;
